import React, { useState, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Text from '@/components/Text';
import VibeAnimation from './VibeAnimation';

interface GaugeSliderProps {
  value: string;
  onChange: (value: string) => void;
  options: {
    label: string;
    value: string;
    text: string;
    description: string;
  }[];
  theme?: 'risk' | 'time';
}

export default function GaugeSlider({ value, onChange, options, theme = 'risk' }: GaugeSliderProps) {
  const [activeIndex, setActiveIndex] = useState(() => {
    const found = options.findIndex(opt => opt.value === value);
    return found !== -1 ? found : 0;
  });
  
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const getVibeStyles = (index: number) => {
    const ratio = index / (options.length - 1);
    
    if (theme === 'time') {
      // Time Theme: 0: Instant (Fast/Flashy), 1: Legacy (Slow/Massive/Deep)
      if (ratio <= 0.25) return {
        color: 'text-amber-400',
        rgb: 'rgb(251, 191, 36)',
        glow: 'shadow-amber-500/80',
        bg: 'bg-amber-400',
        animation: 'animate-ping duration-300 opacity-20',
        speed: 'duration-150',
        label: 'Short'
      };
      if (ratio <= 0.5) return {
        color: 'text-orange-400',
        rgb: 'rgb(251, 146, 60)',
        glow: 'shadow-orange-500/50',
        bg: 'bg-orange-400',
        animation: 'animate-pulse duration-500 opacity-30',
        speed: 'duration-300',
        label: 'Steady'
      };
      if (ratio <= 0.75) return {
        color: 'text-indigo-400',
        rgb: 'rgb(129, 140, 248)',
        glow: 'shadow-indigo-500/50',
        bg: 'bg-indigo-400',
        animation: 'animate-[pulse_3s_ease-in-out_infinite]',
        speed: 'duration-700',
        label: 'Growth'
      };
      return {
        color: 'text-violet-500',
        rgb: 'rgb(139, 92, 246)',
        glow: 'shadow-violet-600/100',
        bg: 'bg-violet-600',
        animation: 'animate-[pulse_5s_ease-in-out_infinite] scale-150',
        speed: 'duration-1000',
        label: 'Legacy'
      };
    }

    // Default Risk Theme: 0: Safety (Blue/Green), 1: Chaos (Teal/Pink/Violet)
    if (ratio <= 0.25) return {
      color: 'text-emerald-400',
      rgb: 'rgb(52, 211, 153)',
      glow: 'shadow-emerald-500/50',
      bg: 'bg-emerald-400',
      animation: 'animate-[pulse_4s_ease-in-out_infinite]',
      speed: 'duration-1000'
    };
    if (ratio <= 0.5) return {
      color: 'text-sky-400',
      rgb: 'rgb(56, 189, 248)',
      glow: 'shadow-sky-500/50',
      bg: 'bg-sky-400',
      animation: 'animate-[pulse_3s_ease-in-out_infinite]',
      speed: 'duration-700'
    };
    if (ratio <= 0.75) return {
      color: 'text-teal-400',
      rgb: 'rgb(45, 212, 191)',
      glow: 'shadow-teal-500/50',
      bg: 'bg-teal-400',
      animation: 'animate-[pulse_2s_ease-in-out_infinite]',
      speed: 'duration-500'
    };
    return {
      color: 'text-pink',
      rgb: 'rgb(255, 16, 88)',
      glow: 'shadow-pink/100',
      bg: 'bg-pink',
      animation: 'animate-pulse opacity-40 duration-75 scale-125',
      speed: 'duration-150'
    };
  };

  const vibe = getVibeStyles(activeIndex);

  const handleInteraction = (e: React.PointerEvent) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    
    // Divide into zones based on options length
    const stepSize = 1 / (options.length - 1);
    const index = Math.round(relativeX / stepSize);
    
    if (index !== activeIndex) {
      setActiveIndex(index);
      onChange(options[index].value);
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    handleInteraction(e);
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isDragging) {
      handleInteraction(e);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    setIsDragging(false);
    (e.target as Element).releasePointerCapture(e.pointerId);
  };

  return (
    <div className="w-full py-6 md:py-12 px-4 md:px-8 flex flex-col gap-6 md:gap-12 items-center relative overflow-hidden rounded-[3rem] [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent),linear-gradient(to_right,transparent,black_20%,black_80%,transparent)] [mask-composite:intersect] [-webkit-mask-composite:destination-in]">
      <VibeAnimation 
        vibe={theme === 'time' ? 1 - (activeIndex / (options.length - 1)) : activeIndex / (options.length - 1)} 
        color={vibe.rgb} 
      />
      
      {/* Dynamic Background Vibe */}
      <div className={twMerge(
        "absolute inset-0 -z-10 transition-all duration-1000 blur-[120px] opacity-[0.03]",
        vibe.bg
      )} />
      
      {/* Top Floating Particles based on vibe */}
      <div className="absolute top-0 left-0 w-full h-full -z-10 pointer-events-none">
        <div className={twMerge(
          "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-64 blur-[80px] rounded-full transition-all duration-1000",
          vibe.bg,
          vibe.animation.replace('opacity-40', 'opacity-10').replace('opacity-20', 'opacity-5')
        )} style={{ opacity: 0.05 }} />
      </div>

      {/* Label and Description Area */}
      <div className="flex flex-col items-center gap-3 text-center min-h-[100px] relative z-10 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
        <div 
          key={activeIndex} 
          className={twMerge(
            "animate-in fade-in slide-in-from-bottom-4 duration-500",
            theme === 'time' 
              ? (activeIndex / (options.length - 1)) < 0.25 && "animate-jitter"
              : (activeIndex / (options.length - 1)) > 0.75 && "animate-jitter"
          )}
        >
          <Text size="xl" className={twMerge("font-game uppercase tracking-tighter italic transition-colors duration-500", vibe.color)}>
            {options[activeIndex].label}
          </Text>
          <Text size="sm" className="block text-white mt-2 max-w-xs mx-auto leading-relaxed font-bold">
            {options[activeIndex].text}
          </Text>
          <Text size="sm" className="block text-white/50 mt-1 max-w-xs mx-auto leading-relaxed h-14">
            {options[activeIndex].description}
          </Text>
        </div>
      </div>

      {/* Futuristic Slider Track */}
      <div className="w-full px-10 relative z-10">
        <div 
          ref={sliderRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="relative w-full h-4 cursor-pointer group touch-none"
        >
          {/* Visual Track extension (Full Width) */}
          <div className="absolute inset-y-0 -left-10 -right-10 bg-white/5 rounded-full border border-white/10 -z-10" />

          {/* Active Track Glow - Lower bound always stretched left, right bound follows knob (except at max) */}
          <div 
            className={twMerge(
              "absolute inset-y-0 transition-all duration-500 rounded-full",
              vibe.bg,
              vibe.glow
            )}
            style={{ 
              left: '-2.5rem',
              width: activeIndex === options.length - 1 
                ? 'calc(100% + 5rem)' 
                : `calc(${(activeIndex / (options.length - 1)) * 100}% + 2.5rem)` 
            }}
          />

          {/* Steps / Tick Marks */}
          <div className="absolute inset-0 flex justify-between px-0.5">
            {options.map((_, i) => (
              <div 
                key={i}
                className={twMerge(
                  "h-full w-1 rounded-full transition-all duration-500",
                  i <= activeIndex ? vibe.bg : "bg-white/10"
                )}
              />
            ))}
          </div>

          {/* The Thumb */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-500 ease-out z-20 pointer-events-none"
            style={{ left: `${(activeIndex / (options.length - 1)) * 100}%` }}
          >
            <div className="relative group/thumb">
              {/* Outer Glow */}
              <div className={twMerge("absolute -inset-6 blur-2xl rounded-full transition-all duration-500", vibe.bg, vibe.animation)} />
              
              {/* Thumb Body */}
              <div className={twMerge(
                "size-10 rounded-2xl bg-white border-2 shadow-2xl flex items-center justify-center rotate-45 group-hover/thumb:scale-110 transition-all duration-500",
                vibe.color.replace('text-', 'border-')
              )}>
                {/* Inner core */}
                <div className={twMerge(
                  "size-4 rounded-lg animate-spin-slow transition-colors duration-500",
                  vibe.bg
                )} />
              </div>

              {/* Step Label (Mobile optimized) */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-100 transition-opacity">
                <Text size="xs" className={twMerge("font-black uppercase tracking-widest transition-colors duration-500", vibe.color)}>
                  Level {activeIndex + 1}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Minimalist Instructions */}
      <div className="flex justify-between w-full opacity-30 px-2 z-10">
        <Text size="xs" className="uppercase tracking-widest font-bold">
          {theme === 'time' ? 'Now' : 'Safe'}
        </Text>
        <Text size="xs" className="uppercase tracking-widest font-bold">
          {theme === 'time' ? 'Legacy' : 'Chaos'}
        </Text>
      </div>
    </div>
  );
}

